import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Loader2, Star, Volume2, Sparkles, Lock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function SELCurriculumPage() {
  const { childId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({ completed_lesson_ids: [], total_xp_earned: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [lessonsRes, progressRes] = await Promise.all([
          api.get('/sel/lessons'),
          api.get(`/sel/progress/${childId}`).catch(() => ({ data: { completed_lesson_ids: [], total_xp_earned: 0 } })),
        ]);
        if (cancelled) return;
        setLessons(lessonsRes.data.lessons || []);
        setProgress(progressRes.data || { completed_lesson_ids: [], total_xp_earned: 0 });
      } catch (err) {
        toast.error('Could not load lessons');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [childId, api]);

  const speak = (text) => {
    try {
      if (!('speechSynthesis' in window)) return;
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.rate = 0.95;
      utter.pitch = 1.1;
      window.speechSynthesis.speak(utter);
    } catch (err) { void err; }
  };

  const completedIds = new Set(progress.completed_lesson_ids || []);
  const completedCount = completedIds.size;
  const totalCount = lessons.length || 1;
  const percent = Math.round((completedCount / totalCount) * 100);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-pink-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-rose-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" className="rounded-full shrink-0" onClick={() => navigate(-1)} data-testid="sel-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-fredoka font-bold text-lg text-slate-800 truncate">Being Polite & Kind</h1>
              <p className="text-xs text-slate-500">Social Emotional Learning</p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-rose-100 rounded-full px-3 py-1 shrink-0">
            <Star className="w-4 h-4 text-rose-500" />
            <span className="text-sm font-bold text-rose-700">{progress.total_xp_earned || 0} XP</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 pb-24">
        {/* Progress card */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="border-0 shadow-md overflow-hidden bg-gradient-to-r from-rose-400 via-pink-400 to-amber-400 text-white">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/25 flex items-center justify-center text-3xl shrink-0">
                💖
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold">Kindness Journey</h2>
                <p className="text-sm opacity-90 mb-2">
                  {completedCount} of {totalCount} lessons complete
                </p>
                <Progress value={percent} className="h-2 bg-white/30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Lesson grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="sel-lesson-grid">
          {lessons.map((lesson, index) => {
            const isDone = completedIds.has(lesson.id);
            const prevLesson = lessons[index - 1];
            const isLocked = index > 0 && prevLesson && !completedIds.has(prevLesson.id);
            return (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                whileHover={!isLocked ? { scale: 1.02 } : {}}
                whileTap={!isLocked ? { scale: 0.98 } : {}}
                className={isLocked ? 'opacity-60' : ''}
              >
                <Link
                  to={isLocked ? '#' : `/sel/${childId}/lesson/${lesson.id}`}
                  onClick={(e) => {
                    if (isLocked) {
                      e.preventDefault();
                      toast.info('Complete the previous lesson first');
                    }
                  }}
                  data-testid={`sel-lesson-${lesson.order}`}
                >
                  <Card className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                    <div className={`h-24 bg-gradient-to-br ${lesson.color} flex items-center justify-center text-5xl relative`}>
                      <span>{lesson.emoji}</span>
                      {isDone && (
                        <div className="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                        </div>
                      )}
                      {isLocked && (
                        <div className="absolute top-2 right-2 bg-white/80 rounded-full w-8 h-8 flex items-center justify-center shadow">
                          <Lock className="w-4 h-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">Lesson {lesson.order}</span>
                        <span className="text-xs text-emerald-600 font-medium">+{lesson.xp_reward} XP</span>
                      </div>
                      <h3 className="font-fredoka font-bold text-slate-800 leading-tight mb-2">{lesson.title}</h3>
                      <div className="flex items-center gap-2 text-sm">
                        <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                        <span className="text-slate-600">Magic phrase:</span>
                        <span className="font-semibold text-rose-600">&ldquo;{lesson.phrase}&rdquo;</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-2 h-8 px-2 text-slate-500 hover:text-slate-800"
                        onClick={(e) => { e.preventDefault(); speak(`${lesson.title}. Magic phrase: ${lesson.phrase}`); }}
                        data-testid={`sel-listen-${lesson.order}`}
                      >
                        <Volume2 className="w-4 h-4 mr-1" /> Listen
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
